// Executor do registry de ações.
//
// Único lugar que conhece GlobalBus e router — o registry é dado puro, os
// componentes só emitem intenção. Por isso a mesma ação vale para o botão
// "Executar" da listagem e para o passo clicado na timeline do resumo.
import { GlobalBus } from '@services/bus'
import { VALIDACAO_USABILIDADE } from '@modules/client-journey/constants/journey-flags'
import { montarModalDePasso } from '@modules/client-journey/utils/modal-de-passo/modal-de-passo'
import {
  isClienteModalHostReady,
  waitClienteModalHostReady,
} from '@modules/navegacao-v2/utils/cliente-modal-host'
import {
  ACTION_TYPES,
  HOST,
  resolveStepAction,
} from '@modules/client-journey/utils/journey-step-actions/journey-step-actions'

export const MODULE_KEY = 'client-journey'


/**
 * @param {Object} opts
 * @param {string} opts.actionKey  chave do passo no template
 * @param {Object} opts.client     cliente completo (o modal precisa do objeto)
 * @param {Object} opts.router     this.$router
 * @param {Function} [opts.reload] recarrega a jornada quando o modal concluir
 * @param {Function} [opts.onIndisponivel] chamado quando não há ação
 * @param {Function} [opts.showModal] this.$showModal — usado na fase de
 *   validação de usabilidade, em que cada passo abre o modal-esboço
 * @param {string} [opts.stepLabel]  nome do passo, para o modal-esboço
 * @param {string} [opts.stageLabel] nome da etapa, para o modal-esboço
 * @returns {Promise<boolean>} true quando algo foi disparado
 */
export function executarAcaoDoPasso({
  actionKey,
  client,
  router,
  reload,
  onIndisponivel,
  showModal,
  stepLabel,
  stageLabel,
}) {
  // Fase atual: nenhum passo dispara a ação real. Sai ANTES de resolver o
  // registry porque, aqui, até o passo sem fluxo dedicado deve abrir o modal —
  // é justamente a jornada inteira que está sendo percorrida para validação.
  if (VALIDACAO_USABILIDADE) {
    if (typeof showModal !== 'function') {
      // ATENCAO: NÃO cai no registry. Documentação não é guarda: um chamador
      // novo que esqueça `showModal` dispararia a ação REAL (modal de IPS,
      // navegação) no meio de uma validação de usabilidade, em silêncio.
      // Falhar aqui é barulhento e inofensivo; o contrário é o oposto.
      console.error(
        '[client-journey] executarAcaoDoPasso chamado sem `showModal` com ' +
          'VALIDACAO_USABILIDADE ligada. A ação real NÃO foi disparada. ' +
          'Passe `showModal` (ver CLAUDE.md do módulo, §9). actionKey=' +
          String(actionKey)
      )
      return Promise.resolve(false)
    }
    showModal(montarModalDePasso({ stepLabel: stepLabel, stageLabel: stageLabel }))
    return Promise.resolve(true)
  }

  return executarAcaoReal({ actionKey, client, router, reload, onIndisponivel })
}

/**
 * O caminho DEFINITIVO: resolve o registry e dispara modal ou rota.
 *
 * Exportado à parte por dois motivos: mantém `executarAcaoDoPasso` dentro do
 * limite de statements do ESLint, e deixa o registry testável enquanto
 * `VALIDACAO_USABILIDADE` estiver ligada — sem isso, o código que vai valer na
 * próxima fase ficaria sem cobertura até alguém virar a constante.
 *
 * @param {Object} opts mesmos campos de `executarAcaoDoPasso`, sem os do esboço
 * @returns {Promise<boolean>} true quando algo foi disparado
 */
export function executarAcaoReal({
  actionKey,
  client,
  router,
  reload,
  onIndisponivel,
}) {
  const acao = resolveStepAction(actionKey)

  if (acao.type === ACTION_TYPES.NONE) {
    if (typeof onIndisponivel === 'function') onIndisponivel(acao.reason)
    return Promise.resolve(false)
  }

  if (acao.type === ACTION_TYPES.ROUTE) {
    if (!router) return Promise.resolve(false)
    // `.catch` engole NavigationDuplicated: clicar no passo da rota em que já
    // se está não deve virar erro no console.
    router.push(acao.route({ client })).catch(() => {})
    return Promise.resolve(true)
  }

  return abrirModal({ acao, actionKey, client, router, reload })
}

// Extraído para manter `executarAcaoDoPasso` dentro do limite de statements do
// ESLint (14), que roda como erro no gate.
async function abrirModal({ acao, actionKey, client, router, reload }) {
  const payload = acao.payload ? acao.payload({ client, reload }) : {}

  // Modal hospedado pelo shell DESTE módulo (os de Compliance, que não estão em
  // global-modals.vue). Ele só existe dentro da rota /resumo-cliente: chamado de
  // outra tela, esperar o host seria esperar por algo que nunca monta, e o emit
  // cairia no vazio sem erro nenhum.
  if (acao.host === HOST.CLIENT_JOURNEY && !isClienteModalHostReady(MODULE_KEY)) {
    if (!router) return false
    // Leva o usuário para o resumo do cliente com o passo na query; a página
    // abre a ação ao montar, já com o host disponível e com o contexto do
    // cliente à vista — que é onde a decisão é tomada de qualquer forma.
    router
      .push({
        name: 'client-journey.resumo',
        params: { id: client && client.id },
        query: { passo: actionKey },
      })
      .catch(() => {})
    return true
  }

  if (acao.host === HOST.CLIENT_JOURNEY) {
    await waitClienteModalHostReady(MODULE_KEY, { timeoutMs: 2000 })
  }

  GlobalBus.$emit(acao.modal, payload)
  return true
}
